import {
	Button,
	cn,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
	Textarea,
} from "@monbits/ui";
import { ArrowRight, MessageSquare } from "lucide-react";
import * as React from "react";
import LinkInput, { validateUrl } from "src/components/link-input/link-input";
import {
	CURRENCY,
	type PaymentLinkAction,
	type PaymentLinkParams,
} from "src/types";

interface Props {
	onFinish: (values: PaymentLinkParams) => Promise<void>;
	isFormSubmitting: boolean;
}

interface FormErrors {
	name?: string;
	price?: string;
	successMessage?: string;
	redirectUrl?: string;
}

const minPrice = 0;
const maxPrice = 10 ** 7;
const linkPrefix = "https://";

const PaymentLinkForm: React.FC<Props> = (props: Props) => {
	const nameId = React.useId();
	const priceId = React.useId();
	const descriptionId = React.useId();
	const successMessageId = React.useId();
	const [formData, setFormData] = React.useState<Partial<PaymentLinkParams>>({
		name: "",
		price: undefined,
		currency: "USD",
		description: "",
		successAction: "showMessage",
		successMessage: "",
		redirectUrl: "",
	});

	const [linkAction, setLinkAction] =
		React.useState<PaymentLinkAction>("showMessage");
	const [errors, setErrors] = React.useState<FormErrors>({});

	const handleInputChange = (
		field: keyof Partial<PaymentLinkParams>,
		value: string | number | undefined,
	) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
		// Clear error when user starts typing
		if (errors[field as keyof FormErrors]) {
			setErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value === "") {
			handleInputChange("price", undefined);
			return;
		}
		const numValue = parseFloat(value);
		if (!Number.isNaN(numValue)) {
			handleInputChange(
				"price",
				Math.min(Math.max(numValue, minPrice), maxPrice),
			);
		}
	};

	const handleActionChange = (value: PaymentLinkAction) => {
		setLinkAction(value);
		setFormData((prev) => ({ ...prev, successAction: value }));
	};

	const validateForm = (): boolean => {
		const newErrors: FormErrors = {};

		if (!formData.name?.trim()) {
			newErrors.name = "Field is required";
		}

		if (formData.price === undefined || formData.price === null) {
			newErrors.price = "Field is required";
		} else if (formData.price < minPrice || formData.price > maxPrice) {
			newErrors.price = `Price must be between ${minPrice} and ${maxPrice}`;
		}

		if (linkAction === "showMessage" && !formData.successMessage?.trim()) {
			newErrors.successMessage = "Field is required";
		}

		if (linkAction === "redirect") {
			if (!formData.redirectUrl?.trim()) {
				newErrors.redirectUrl = "Field is required";
			} else if (!validateUrl(formData.redirectUrl, true)) {
				newErrors.redirectUrl = "Please enter a valid URL";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const onSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		const values = { ...formData } as PaymentLinkParams;
		if (linkAction === "redirect") {
			values.redirectUrl = linkPrefix + values.redirectUrl;
			values.successMessage = undefined;
		} else {
			values.redirectUrl = undefined;
		}

		values.successAction = linkAction;

		await props.onFinish(values);
	};

	return (
		<form onSubmit={onSubmit} className="space-y-6">
			{/* Basic Information Section */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
					Basic Information
				</h4>

				<div className="space-y-2">
					<Label htmlFor={nameId}>
						Link Name <span className="text-destructive">*</span>
					</Label>
					<Input
						id={nameId}
						placeholder="e.g., Product Purchase, Donation, Service Fee"
						value={formData.name}
						onChange={(e) => handleInputChange("name", e.target.value)}
						className={errors.name ? "border-destructive" : ""}
					/>
					{errors.name && (
						<p className="text-sm font-medium text-destructive">
							{errors.name}
						</p>
					)}
				</div>

				<div className="space-y-2">
					<Label htmlFor={descriptionId}>
						Description{" "}
						<span className="text-muted-foreground font-normal">
							(optional)
						</span>
					</Label>
					<Textarea
						id={descriptionId}
						placeholder="Add details about what this payment is for..."
						rows={2}
						value={formData.description}
						onChange={(e) => handleInputChange("description", e.target.value)}
					/>
				</div>
			</div>

			<Separator />

			{/* Pricing Section */}
			<div className="space-y-4">
				<h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
					Pricing
				</h4>

				<div className="grid grid-cols-[3fr_1fr] gap-3">
					<div className="space-y-2">
						<Label htmlFor={priceId}>
							Amount <span className="text-destructive">*</span>
						</Label>
						<Input
							id={priceId}
							type="number"
							step="0.01"
							min={minPrice}
							max={maxPrice}
							placeholder="0.00"
							value={formData.price ?? ""}
							onChange={handlePriceChange}
							className={errors.price ? "border-destructive" : ""}
						/>
						{errors.price && (
							<p className="text-sm font-medium text-destructive">
								{errors.price}
							</p>
						)}
					</div>
					<div className="space-y-2">
						<Label>Currency</Label>
						<Select
							value={formData.currency}
							onValueChange={(value) => handleInputChange("currency", value)}
						>
							<SelectTrigger className="w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CURRENCY.map((currency) => (
									<SelectItem key={currency} value={currency}>
										{currency}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
			</div>

			<Separator />

			{/* After Payment Section */}
			<div className="space-y-4">
				<div>
					<h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
						After Payment
					</h4>
					<p className="text-xs text-muted-foreground mt-1">
						What happens after a successful payment?
					</p>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<button
						type="button"
						onClick={() => handleActionChange("showMessage")}
						className={cn(
							"flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
							linkAction === "showMessage"
								? "border-primary bg-primary/5"
								: "border-border hover:border-muted-foreground/50",
						)}
					>
						<div
							className={cn(
								"w-10 h-10 rounded-full flex items-center justify-center",
								linkAction === "showMessage"
									? "bg-primary/10 text-primary"
									: "bg-muted text-muted-foreground",
							)}
						>
							<MessageSquare className="h-5 w-5" />
						</div>
						<div className="text-center">
							<div className="text-sm font-medium">Show Message</div>
							<div className="text-xs text-muted-foreground">
								Display a thank you message
							</div>
						</div>
					</button>

					<button
						type="button"
						onClick={() => handleActionChange("redirect")}
						className={cn(
							"flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all",
							linkAction === "redirect"
								? "border-primary bg-primary/5"
								: "border-border hover:border-muted-foreground/50",
						)}
					>
						<div
							className={cn(
								"w-10 h-10 rounded-full flex items-center justify-center",
								linkAction === "redirect"
									? "bg-primary/10 text-primary"
									: "bg-muted text-muted-foreground",
							)}
						>
							<ArrowRight className="h-5 w-5" />
						</div>
						<div className="text-center">
							<div className="text-sm font-medium">Redirect</div>
							<div className="text-xs text-muted-foreground">
								Send to your website
							</div>
						</div>
					</button>
				</div>

				{linkAction === "redirect" ? (
					<LinkInput
						placeholder="your-store.com/thank-you"
						label="Redirect URL"
						name="redirectUrl"
						required
						value={formData.redirectUrl}
						onChange={(value) => handleInputChange("redirectUrl", value)}
						error={errors.redirectUrl}
					/>
				) : (
					<div className="space-y-2">
						<Label htmlFor={successMessageId}>
							Success Message <span className="text-destructive">*</span>
						</Label>
						<Textarea
							id={successMessageId}
							placeholder="Thank you for your payment! Your order has been confirmed."
							rows={2}
							value={formData.successMessage}
							onChange={(e) =>
								handleInputChange("successMessage", e.target.value)
							}
							className={errors.successMessage ? "border-destructive" : ""}
						/>
						{errors.successMessage && (
							<p className="text-sm font-medium text-destructive">
								{errors.successMessage}
							</p>
						)}
					</div>
				)}
			</div>

			{/* Hidden submit button for form validation */}
			<Button type="submit" className="sr-only">
				Submit
			</Button>
		</form>
	);
};

export default PaymentLinkForm;
